import React, { useContext, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

// mui
import { styled } from "@material-ui/styles";
import { Collapse } from "@mui/material";
import List from "@mui/material/List";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";

// component
import Grid from "@mui/material/Grid";
import ListItemImgArea from "../../../../../../../../common/components/listitem/ListItemImgArea";
import OcrButton from "./ocrButton/OcrButton";
import TopTool from "./TopTool";
import CustomImage from "./customImage/CustomImage";
import DefaultContent from "./customImgContent/DefaultContent";
import UploadContent from "./customImgContent/UploadContent";
import CustomExpandBtn from "./customExpandbtn/customExpandBtn";
import CustomMenuItem from "./customMenuItem/CustomMenuItem";

// action
import Act from "../../../../../../../../store/actions";
import { StoreContext } from "../../../../../../../../store/StoreProvider";
import { isEmpty } from "../../../../../../../../common/utils";
import { OCR_CONTENT, OCR_RESULT_STATUS } from "../../../Config";

const StyledBox = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    height: "calc(100% - 108px)",
    border: "1px #CECECE solid",
    borderRadius: "8px",
    marginTop: "1rem",
    marginBottom: "1rem",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "F7F8FA",
  },
}));

const StyledDefaultBox = styled(Box)(({ theme }) => ({
  "&.MuiBox-root": {
    position: "absolute",
    zIndex: "2",
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: " #F7F8FA",
  },
}));

const StyledCollapse = styled(Collapse)(({ theme }) => ({
  "&.MuiCollapse-root": {
    position: "absolute",
    zIndex: "4",
    background: "white",
    width: "100%",
    maxHeight: "calc(100% - 45px)",
    overflowY: "auto",
  },
}));

const ImageArea = () => {
  const [state, dispatch] = useContext(StoreContext);
  const { digitalAI } = state;
  const { ocrImgList, pagination, uploadOcrStatus, ocrErrorCount } = digitalAI;
  const { activePage } = pagination;

  const [open, setOpen] = useState(false);
  const [focusItem, setFocusItem] = useState();
  const intl = useIntl();

  useEffect(() => {
    setFocusItem(ocrImgList[activePage]);
  }, [activePage]);

  useEffect(() => {
    if (!isEmpty(ocrImgList) && isEmpty(focusItem)) {
      dispatch({
        type: Act.DIGITAL_AI_SET_ACTIVE_PAGE,
        payload: 0,
      });
      setFocusItem(ocrImgList[0]);
    }
  }, [ocrImgList]);

  useEffect(() => {
    if (uploadOcrStatus) {
      dispatch({
        type: Act.DIGITAL_AI_INIT,
      });
      setFocusItem(null);
    }
  }, [uploadOcrStatus]);

  const handleClickImgList = (imgObj, index) => {
    dispatch({
      type: Act.DIGITAL_AI_SET_ACTIVE_PAGE,
      payload: index,
    });

    setOpen(false);
  };

  const handleCheckAll = (event) => {
    const tmpArr = JSON.parse(JSON.stringify(ocrImgList));

    const allCheck = tmpArr.map((el) => {
      if (!(OCR_CONTENT in el)) {
        return {
          ...el,
          checked: event.target.checked,
        };
      }
      return el;
    });

    dispatch({
      type: Act.DIGITAL_AI_SET_IMG_LIST,
      payload: allCheck,
    });
  };

  const handleCheckOne = (item) => {
    const tmpArr = JSON.parse(JSON.stringify(ocrImgList));

    const oneCheck = tmpArr.map((el) => {
      if (el.imgUrl === item.imgUrl) {
        return { ...el, checked: !el.checked };
      }
      return el;
    });

    dispatch({
      type: Act.DIGITAL_AI_SET_IMG_LIST,
      payload: oneCheck,
    });
  };

  const handleDelete = (item) => {
    const deleteArr = ocrImgList.filter((el) => !(el.imgUrl === item.imgUrl));

    // 當刪除的是當前點選的圖片時，帶入刪除後的第一張
    if (focusItem.imgUrl === item.imgUrl) {
      const curImgIndex = activePage === 0 ? activePage : activePage - 1;

      dispatch({
        type: Act.DIGITAL_AI_SET_ACTIVE_PAGE,
        payload: curImgIndex,
      });
    } else {
      dispatch({
        type: Act.DIGITAL_AI_SET_ACTIVE_PAGE,
        payload: activePage - 1,
      });
    }

    dispatch({
      type: Act.DIGITAL_OCR_ERROR_COUNT,
      payload:
        deleteArr.filter(
          (el) => OCR_RESULT_STATUS in el && !el[OCR_RESULT_STATUS]
        ).length || null,
    });

    dispatch({
      type: Act.DIGITAL_AI_SET_IMG_LIST,
      payload: deleteArr,
    });

    dispatch({
      type: Act.DIGITAL_AI_SET_TOTAL_PAGE,
      payload: deleteArr.length,
    });
  };

  const imageDisplay = useMemo(
    () =>
      ocrImgList.map((imgObj, index) => ({
        ...imgObj,
        listItemButtonProps: {
          onClick: () => handleClickImgList(imgObj, index),
        },
        listItemProps: {
          secondaryAction: (
            <CustomMenuItem imgObj={imgObj} handleDelete={handleDelete} />
          ),
        },
        listIcon: (
          <Checkbox
            disabled={imgObj.checked && OCR_CONTENT in imgObj}
            checked={imgObj.checked}
            onClick={() => handleCheckOne(imgObj)}
          />
        ),
        sx: {
          "& .MuiBadge-root": {
            maxWidth: "calc(100% - 42px)",
          },
        },
        textPrimary: imgObj.imgUrl,
      })),
    [ocrImgList, focusItem]
  );

  return (
    <Grid
      item
      xs
      sx={{
        marginRight: "8px",
        backgroundColor: "#FFFFFF",
        padding: "16px 24px",
        borderRadius: "8px",
        boxShadow: "0 8px 16px 0 #096C9B1A",
        height: "calc(100vh - 170px)",
      }}
    >
      {/* title and top tool */}
      <TopTool setFocusItem={setFocusItem} />
      {/* Image list and display image area */}
      <StyledBox>
        {/* uploading is true */}
        {uploadOcrStatus && (
          <StyledDefaultBox>
            <UploadContent />
          </StyledDefaultBox>
        )}
        {/* default Upload description for file limit */}
        {isEmpty(ocrImgList) && !uploadOcrStatus && (
          <StyledDefaultBox>
            <DefaultContent />
          </StyledDefaultBox>
        )}
        <List
          component="nav"
          disablePadding
          sx={{ position: "sticky", zIndex: "3" }}
        >
          <ListItemImgArea
            sx={{ backgroundColor: "#fff" }}
            badgeListProps={{
              badgeContent: ocrErrorCount,
              color: "error",
            }}
            listItemProps={{
              secondaryAction: (
                <CustomExpandBtn open={open} setOpen={setOpen} />
              ),
            }}
            listItemTextProps={{
              sx: {
                fontWeight: "700",
              },
            }}
            listItemButtonProps={{
              onClick: () => setOpen(!open),
              disabled: isEmpty(focusItem),
            }}
            listIcon={
              <Checkbox
                disabled={isEmpty(focusItem)}
                onClick={handleCheckAll}
                indeterminate={
                  (ocrImgList.some((el) => el.checked) &&
                    !ocrImgList.every((el) => el.checked === true)) ||
                  ocrImgList.some((el) => OCR_CONTENT in el)
                }
                checked={
                  !isEmpty(ocrImgList) &&
                  ocrImgList.every((el) => el.checked === true)
                }
              />
            }
            textPrimary={intl.formatMessage({
              id: "sna.sidetool.digitalAITool.ocr.listTitle",
              defaultMessage: "File list",
            })}
          />
        </List>
        {/* dropdown list */}
        <StyledCollapse in={open}>
          {imageDisplay.map((imgObj) => (
            <ListItemImgArea
              key={imgObj.imgUrl}
              listItemButtonProps={imgObj.listItemButtonProps}
              listItemProps={imgObj.listItemProps}
              listIcon={imgObj.listIcon}
              badgeListProps={imgObj.badgeListProps}
              textPrimary={imgObj.textPrimary}
            />
          ))}
        </StyledCollapse>
        {/* Image Area  */}
        <CustomImage focusItem={focusItem} />
      </StyledBox>
      <OcrButton setFocusItem={setFocusItem} />
    </Grid>
  );
};

export default ImageArea;
